
import type { State, ContractType, TradeDirection, TradeDurationUnit, TradeMarker, TradeStatus } from "./types";
import { durationToMs } from "./trades";
import { markStaticDirty } from "./dirty";

// ========== Types ==========
export type { ContractType, TradeDirection, TradeDurationUnit, TradeMarker, TradeStatus };

// ========== Contract Management - CRUD Operations ==========

/**
 * Generate unique contract ID
 */
export function generateContractId(): string {
  return `contract_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Find contract by ID or contractId
 */
export function findContractById(state: State, id: string | number): TradeMarker | undefined {
  return state.trades.find(t => t.contractId === id || t.id === id);
}

/**
 * Find contract by custom ID (alias for findContractById)
 */
export function getContract(state: State, id: string | number): TradeMarker | undefined {
  return findContractById(state, id);
}

/**
 * Add contract to state (low-level)
 */
export function addContractToState(state: State, contract: TradeMarker): void {
  state.trades.push(contract);
}

/**
 * Remove contract from state
 */
export function removeContractFromState(state: State, id: string | number): boolean {
  const index = state.trades.findIndex(t => t.contractId === id || t.id === id);
  if (index === -1) return false;
  state.trades.splice(index, 1);
  return true;
}

/**
 * Update contract in state
 */
export function updateContractInState(state: State, id: string | number, updates: Partial<TradeMarker>): TradeMarker | undefined {
  const contract = findContractById(state, id);
  if (!contract) return undefined;
  Object.assign(contract, updates);
  return contract;
}

// ========== Contract Creation - Standalone Field Setters ==========

/**
 * Set contract ID
 */
export function setContractId(contract: TradeMarker, id: string | number): void {
  contract.contractId = id;
}

/**
 * Set contract type
 */
export function setContractType(contract: TradeMarker, type: ContractType): void {
  contract.contractType = type;
}

/**
 * Set trade direction
 */
export function setDirection(contract: TradeMarker, direction: TradeDirection): void {
  contract.direction = direction;
}

/**
 * Set entry price
 */
export function setEntryPrice(contract: TradeMarker, price: number): void {
  contract.entryPrice = price;
}

/**
 * Set entry time (epoch ms)
 */
export function setEntryTime(contract: TradeMarker, timeMs: number): void {
  contract.entryTime = timeMs;
}

/**
 * Set duration value and unit separately
 */
export function setDurationValue(contract: TradeMarker, duration: number): void {
  contract.durationValue = duration;
  contract.durationMs = durationToMs(duration, contract.durationUnit);
}

export function setDurationUnit(contract: TradeMarker, unit: TradeDurationUnit): void {
  contract.durationUnit = unit;
  contract.durationMs = durationToMs(contract.durationValue, unit);
}

/**
 * Set both duration and unit in one call
 */
export function setDuration(contract: TradeMarker, duration: number, unit: TradeDurationUnit): void {
  contract.durationValue = duration;
  contract.durationUnit = unit;
  contract.durationMs = durationToMs(duration, unit);
}

/**
 * Set expiry time
 */
export function setExpiryTime(contract: TradeMarker, expiryTimeMs: number): void {
  contract.expiryTime = expiryTimeMs;
}

/**
 * Set activation price
 */
export function setActivationPrice(contract: TradeMarker, price: number): void {
  contract.activationPrice = price;
}

/**
 * Set activation time
 */
export function setActivationTime(contract: TradeMarker, timeMs: number): void {
  contract.activationTime = timeMs;
}

/**
 * Set exit price
 */
export function setExitPrice(contract: TradeMarker, price: number): void {
  contract.exitPrice = price;
}

/**
 * Set trade status
 */
export function setTradeStatus(contract: TradeMarker, status: TradeStatus): void {
  contract.status = status;
}

/**
 * Set resolved time
 */
export function setResolvedTime(contract: TradeMarker, timeMs: number): void {
  contract.resolvedAt = timeMs;
}

/**
 * Set result source
 */
export function setResultSource(contract: TradeMarker, source: "chart" | "server"): void {
  contract.resultSource = source;
}

/**
 * Set the result of a settled trade (won or lost)
 * This automatically sets resolvedAt to current time if not provided
 */
export function setResult(
  contract: TradeMarker, 
  result: "won" | "lost" | "win" | "loss",
  resolvedTime?: number
): void {
  const normalizedResult = (result === "win" || result === "won") ? "won" : "lost";
  contract.status = normalizedResult;
  contract.resolvedAt = resolvedTime ?? Date.now();
}

/**
 * Set result with server source
 */
export function setServerResult(
  contract: TradeMarker,
  result: "won" | "lost" | "win" | "loss",
  settledTime: number,
  settlementTickId?: string
): void {
  const normalizedResult = (result === "win" || result === "won") ? "won" : "lost";
  contract.status = normalizedResult;
  contract.resolvedAt = settledTime;
  contract.resultSource = "server";
  if (settlementTickId) contract.settlementTickId = settlementTickId;
}

/**
 * Set result with chart source (auto-resolved)
 */
export function setChartResult(
  contract: TradeMarker,
  result: "won" | "lost" | "win" | "loss",
  resolvedTime: number
): void {
  const normalizedResult = (result === "win" || result === "won") ? "won" : "lost";
  contract.status = normalizedResult;
  contract.resolvedAt = resolvedTime;
  contract.resultSource = "chart";
}

/**
 * Set settlement tick ID
 */
export function setSettlementTickId(contract: TradeMarker, tickId: string): void {
  contract.settlementTickId = tickId;
}

// ========== Barrier Management ==========

/**
 * Set single barrier (for HIGH, LOW, KNOCK_IN, KNOCK_OUT)
 */
export function setBarrier(contract: TradeMarker, barrier: number): void {
  contract.barrier = barrier;
}

/**
 * Set barrier low (for ENDS_BETWEEN, ENDS_OUTSIDE, STAYS_IN, STAYS_OUT)
 */
export function setBarrierLow(contract: TradeMarker, barrierLow: number): void {
  contract.barrierLow = barrierLow;
}

/**
 * Set barrier high
 */
export function setBarrierHigh(contract: TradeMarker, barrierHigh: number): void {
  contract.barrierHigh = barrierHigh;
}

/**
 * Set both barriers
 */
export function setBarrierRange(contract: TradeMarker, low: number, high: number): void {
  contract.barrierLow = low;
  contract.barrierHigh = high;
}

// In contractLifecycle.ts

/**
 * Set barrier margin to avoid being hidden behind controls
 * @param state - Chart state
 * @param topMargin - Pixels from top of chart (default: 60)
 * @param bottomMargin - Pixels from bottom of chart (default: 40)
 */
export function setBarrierMargins(state: State, topMargin: number = 60, bottomMargin: number = 40): void {
  state.barrierTopMargin = topMargin;
  state.barrierBottomMargin = bottomMargin;
}

// ========== Digit Contract Management ==========

/**
 * Set digit target (for MATCHES, DIFFERS, OVER, UNDER)
 */
export function setDigitTarget(contract: TradeMarker, target: number): void {
  contract.digitTarget = target;
}

/**
 * Set digit low (for RANGE_IN, RANGE_OUT)
 */
export function setDigitLow(contract: TradeMarker, low: number): void {
  contract.digitLow = low;
}

/**
 * Set digit high
 */
export function setDigitHigh(contract: TradeMarker, high: number): void {
  contract.digitHigh = high;
}

/**
 * Set digit range
 */
export function setDigitRange(contract: TradeMarker, low: number, high: number): void {
  contract.digitLow = low;
  contract.digitHigh = high;
}

// ========== Query Functions ==========

/**
 * Get all trades
 */
export function getAllTrades(state: State): TradeMarker[] {
  return [...state.trades];
}

/**
 * Get active trades
 */
export function getActiveTrades(state: State): TradeMarker[] {
  return state.trades.filter(t => t.status === "active");
}

/**
 * Get won trades
 */
export function getWonTrades(state: State): TradeMarker[] {
  return state.trades.filter(t => t.status === "won");
}

/**
 * Get lost trades
 */
export function getLostTrades(state: State): TradeMarker[] {
  return state.trades.filter(t => t.status === "lost");
}

/**
 * Get trades by contract type
 */
export function getTradesByType(state: State, contractType: ContractType): TradeMarker[] {
  return state.trades.filter(t => t.contractType === contractType);
}

/**
 * Get trades by time range
 */
export function getTradesByTimeRange(state: State, startTimeMs: number, endTimeMs: number): TradeMarker[] {
  return state.trades.filter(t => t.entryTime >= startTimeMs && t.entryTime <= endTimeMs);
}

/**
 * Get trades by direction
 */
export function getTradesByDirection(state: State, direction: TradeDirection): TradeMarker[] {
  return state.trades.filter(t => t.direction === direction);
}

/**
 * Check if there are any active trades
 */
export function hasActiveTrades(state: State): boolean {
  return state.trades.some(t => t.status === "active");
}

/**
 * Get trade count by status
 */
export function getTradeCountByStatus(state: State): { active: number; won: number; lost: number } {
  return {
    active: state.trades.filter(t => t.status === "active").length,
    won: state.trades.filter(t => t.status === "won").length,
    lost: state.trades.filter(t => t.status === "lost").length,
  };
}

// ========== Utility Functions ==========

/**
 * Get current price from chart data
 */
export function getCurrentPrice(state: State): number | null {
  const lastTick = state.chartData[state.chartData.length - 1];
  return lastTick?.quote ?? null;
}

/**
 * Get current time (latest tick time or Date.now())
 */
export function getCurrentTime(state: State): number {
  const lastTick = state.chartData[state.chartData.length - 1];
  return lastTick ? lastTick.epoch * 1000 : Date.now();
}

/**
 * Calculate duration in milliseconds
 */
export function calculateDurationMs(duration: number, unit: TradeDurationUnit): number {
  return durationToMs(duration, unit);
}

// Add to contractLifecycle.ts

/**
 * Set activation point (draws vertical line and dot)
 */
export function setActivationPoint(state: State, contractId: string | number, price: number, timeMs: number): void {
  const contract = findContractById(state, contractId);
  if (!contract) return;
  
  contract.activationPrice = price;
  contract.activationTime = timeMs;
  
  // Mark chart dirty to redraw
  if ((state as any).__redraw) (state as any).__redraw();
}

/**
 * Set exit point (draws flag and vertical line)
 */
export function setExitPoint(state: State, contractId: string | number, price: number, timeMs: number): void {
  const contract = findContractById(state, contractId);
  if (!contract) return;
  
  contract.exitPrice = price;
  contract.resolvedAt = timeMs;
  
  // Mark chart dirty to redraw
  if ((state as any).__redraw) (state as any).__redraw();
}

/**
 * Get contract activation point
 */
export function getActivationPoint(contract: TradeMarker): { price: number; time: number } | null {
  if (contract.activationPrice === undefined || contract.activationTime === undefined) return null;
  return { price: contract.activationPrice, time: contract.activationTime };
}

/**
 * Get contract exit point
 */
export function getExitPoint(contract: TradeMarker): { price: number; time: number } | null {
  if (contract.exitPrice === undefined || contract.resolvedAt === undefined) return null;
  return { price: contract.exitPrice, time: contract.resolvedAt };
}

/**
 * Check if contract has been activated
 */
export function isContractActivated(contract: TradeMarker): boolean {
  return contract.activationPrice !== undefined && contract.activationTime !== undefined;
}

/**
 * Check if contract has been settled
 */
export function isContractSettled(contract: TradeMarker): boolean {
  return contract.exitPrice !== undefined && contract.resolvedAt !== undefined;
}

/**
 * Get time remaining on contract (ms)
 */
export function getTimeRemaining(contract: TradeMarker, currentTimeMs: number = Date.now()): number {
  const endTime = contract.expiryTime;
  return Math.max(0, endTime - currentTimeMs);
}

// Add to contractLifecycle.ts

/**
 * Set activation point with duration tracking
 */
export function setActivationWithDuration(
  state: State, 
  contractId: string | number, 
  price: number, 
  activationTimeMs: number,
  durationMs?: number
): void {
  const contract = findContractById(state, contractId);
  if (!contract) return;
  
  contract.activationPrice = price;
  contract.activationTime = activationTimeMs;
  
  // If duration provided, update expiry time from activation
  if (durationMs !== undefined) {
    contract.expiryTime = activationTimeMs + durationMs;
  }
  
  // Mark chart dirty to redraw
  if ((state as any).__redraw) (state as any).__redraw();
}

/**
 * Get time remaining from activation (not entry)
 */
export function getTimeRemainingFromActivation(contract: TradeMarker, currentTimeMs: number = Date.now()): number {
  const endTime = contract.expiryTime;
  return Math.max(0, endTime - currentTimeMs);
}


/**
 * Check if trade is expired
 */
export function isTradeExpired(contract: TradeMarker, currentTimeMs: number = Date.now()): boolean {
  return currentTimeMs >= contract.expiryTime;
}

/**
 * Check if trade is active
 */
export function isTradeActive(contract: TradeMarker): boolean {
  return contract.status === "active";
}

/**
 * Get trade result (for settled trades)
 */
export function getTradeResult(contract: TradeMarker): "won" | "lost" | null {
  if (contract.status === "active") return null;
  return contract.status === "won" ? "won" : "lost";
}



/**
 * Set the position of price labels and adjust gutters
 * @param state - Chart state
 * @param position - "left" (Wise Trader style) or "right" (default Web Trader style)
 */

export function setPricePosition(state: State, position: "left" | "right" | "both"): void {
  if (position === "left") {
    // Price labels on left - inside the chart
    state.left = 4;      // Small padding for label inset
    state.right = 4;     // Small right padding
    state.pricePosition = "left";
  } else if (position === "right") {
    // Price labels on right - OUTSIDE the chart (need margin)
    state.left = 4;      // Small left padding
    state.right = 60;    // Space for price labels in right margin
    state.pricePosition = "right";
  } else if (position === "both") {
    // Price labels on both sides - left inside, right outside
    state.left = 4;      // Small padding for left label inset
    state.right = 60;    // Space for price labels in right margin
    state.pricePosition = "both";
  }
  markStaticDirty();
}

// ========== Batch Operations ==========

/**
 * Clear all trades
 */
export function clearAllTrades(state: State): void {
  state.trades = [];
}

/**
 * Clear trades by status
 */
export function clearTradesByStatus(state: State, status: TradeStatus): void {
  state.trades = state.trades.filter(t => t.status !== status);
}

/**
 * Clear settled trades (won + lost)
 */
export function clearSettledTrades(state: State): void {
  state.trades = state.trades.filter(t => t.status === "active");
}

// In contractLifecycle.ts - add this function

/**
 * Clean up orphaned trades (created but never activated or settled)
 * @param state - Chart state
 * @param maxAgeMs - Maximum age in milliseconds (default: 30000 = 30 seconds)
 * @returns Number of trades cleaned up
 */
export function cleanupOrphanedTrades(state: State, maxAgeMs: number = 30000): number {
  const now = Date.now();
  const beforeCount = state.trades.length;
  
  state.trades = state.trades.filter((trade) => {
    // Keep trades that are:
    // 1. Active and have been activated (activationTime set)
    // 2. Won or lost (settled) - these will be cleaned by the render loop
    // 3. Created but not activated - remove if older than maxAgeMs
    // 4. Created but not expired - keep if within maxAgeMs
    
    // If trade is won or lost, keep it (render loop will clean after 2.5s)
    if (trade.status === "won" || trade.status === "lost") {
      return true;
    }
    
    // If trade is active and has activation time, keep it
    if (trade.status === "active" && trade.activationTime !== undefined) {
      return true;
    }
    
    // If trade is active but NOT activated yet (just created)
    // Check if it's older than maxAgeMs
    if (trade.status === "active" && trade.activationTime === undefined) {
      const age = now - trade.entryTime;
      // Keep if still within age limit, remove if too old
      return age <= maxAgeMs;
    }
    
    // Default: keep trade
    return true;
  });
  
  const removed = beforeCount - state.trades.length;
  if (removed > 0) {
    console.log(`Cleaned up ${removed} orphaned trades`);
  }
  
  return removed;
}

/**
 * Force clean all non-active trades immediately (for navigation)
 */
export function forceCleanAllTrades(state: State): number {
  const beforeCount = state.trades.length;
  
  state.trades = state.trades.filter((trade) => {
    // Only keep active trades that have been activated
    return trade.status === "active" && trade.activationTime !== undefined;
  });
  
  const removed = beforeCount - state.trades.length;
  if (removed > 0) {
    console.log(`Force cleaned ${removed} trades`);
  }
  
  return removed;
}

// ========== Styling Functions ==========

/**
 * Set trade pin visibility
 */
export function setPinVisible(state: State, visible: boolean): void {
  state.tradeStyles.pin = visible;
}

/**
 * Set vertical line visibility
 */
export function setVerticalLinesVisible(state: State, visible: boolean): void {
  state.tradeStyles.vertical = visible;
}

/**
 * Set zone fill visibility
 */
export function setZoneFillVisible(state: State, visible: boolean): void {
  state.tradeStyles.zone = visible;
}

/**
 * Set countdown visibility
 */
export function setCountdownVisible(state: State, visible: boolean): void {
  state.tradeStyles.countdown = visible;
}

/**
 * Set animation visibility
 */
export function setAnimationVisible(state: State, visible: boolean): void {
  state.tradeStyles.animation = visible;
}

/**
 * Set all trade styles at once
 */
export function setTradeStyles(
  state: State,
  styles: Partial<{
    pin: boolean;
    vertical: boolean;
    zone: boolean;
    countdown: boolean;
    animation: boolean;
  }>
): void {
  Object.assign(state.tradeStyles, styles);
}

// ========== Flag Management ==========

/**
 * Set flag visibility for a specific trade
 * @param contract - The trade contract
 * @param visible - Whether the flag should be shown
 */
export function setFlagVisible(contract: TradeMarker, visible: boolean): void {
  contract.flagVisible = visible;
}

/**
 * Set flag position (override default exit position)
 * @param contract - The trade contract
 * @param price - Price level for the flag
 * @param timeMs - Time for the flag position
 */
export function setFlagPosition(contract: TradeMarker, price: number, timeMs: number): void {
  contract.flagPrice = price;
  contract.flagTime = timeMs;
}

/**
 * Set flag color based on result
 * @param contract - The trade contract
 * @param result - "win" or "loss"
 */
export function setFlagColor(contract: TradeMarker, result: "win" | "loss"): void {
  contract.flagColor = result === "win" ? "#16a34a" : "#dc2626";
}

/**
 * Show flag at exit point (convenience function)
 * @param state - Chart state
 * @param contractId - Contract ID
 * @param exitPrice - Price at exit
 * @param exitTime - Time at exit
 * @param result - "win" or "loss"
 */
export function showFlagAtExit(
  state: State,
  contractId: string | number,
  exitPrice: number,
  exitTime: number,
  result: "win" | "loss"
): void {
  const contract = findContractById(state, contractId);
  if (!contract) return;
  
  contract.exitPrice = exitPrice;
  contract.resolvedAt = exitTime;
  contract.status = result === "win" ? "won" : "lost";
  contract.flagVisible = true;
  contract.flagPrice = exitPrice;
  contract.flagTime = exitTime;
  contract.flagColor = result === "win" ? "#16a34a" : "#dc2626";
  contract.resultSource = "server";
  
  // Mark chart dirty to redraw
  if ((state as any).__redraw) (state as any).__redraw();
}

/**
 * Hide flag for a specific trade
 */
export function hideFlag(contract: TradeMarker): void {
  contract.flagVisible = false;
}

/**
 * Check if flag is visible
 */
export function isFlagVisible(contract: TradeMarker): boolean {
  return contract.flagVisible !== false;
}

// ========== Contract Lifecycle High-Level Functions ==========

/**
 * Create a new contract with all options (one-shot)
 */
/**
 * Create a new contract - ONLY stores what backend provides
 * Do NOT generate expiry times or activation times
 */
export function createContract(
  state: State,
  options: {
    contractType: ContractType;
    duration: number;
    durationUnit?: TradeDurationUnit;
    direction?: TradeDirection;
    entryPrice?: number;
    entryTime?: number;
    contractId?: string | number;
    expiryTime?: number;
    barrier?: number;
    barrierLow?: number;
    barrierHigh?: number;
    digitTarget?: number;
    digitLow?: number;
    digitHigh?: number;
  }
): TradeMarker {
  const lastTick = state.chartData[state.chartData.length - 1];
  
  const entryPrice = options.entryPrice ?? lastTick?.quote;
  if (!entryPrice || !Number.isFinite(entryPrice)) {
    throw new Error("Contract requires entryPrice");
  }

  // Use backend values - do NOT generate our own
  const entryTime = options.entryTime ?? (lastTick ? lastTick.epoch * 1000 : undefined);
  if (!entryTime) {
    throw new Error("Contract requires entryTime from backend");
  }
  
  const expiryTime = options.expiryTime;
  if (!expiryTime) {
    throw new Error("Contract requires expiryTime from backend");
  }

  // Convert offset barriers to absolute values
  const convertBarrier = (value: number | undefined, price: number): number | undefined => {
    if (value === undefined) return undefined;
    if (value < 0 || Math.abs(value) < price * 0.01) {
      return price + value;
    }
    return value;
  };
  
  const barrier = convertBarrier(options.barrier, entryPrice);
  const barrierLow = convertBarrier(options.barrierLow, entryPrice);
  const barrierHigh = convertBarrier(options.barrierHigh, entryPrice);

  const direction = options.direction ?? (
    options.contractType === "DOWN" || options.contractType === "FALL" ? "sell" : "buy"
  );
  const contractId = options.contractId ?? generateContractId();

  const contract: TradeMarker = {
    id: generateContractId(),
    contractId,
    contractType: options.contractType,
    direction,
    placedAt: Date.now(),
    entryTime,
    entryPrice,
    durationMs: expiryTime - entryTime,
    durationValue: options.duration,
    durationUnit: options.durationUnit ?? "s",
    expiryTime,
    barrier: barrier,
    barrierLow: barrierLow,
    barrierHigh: barrierHigh,
    digitTarget: options.digitTarget,
    digitLow: options.digitLow,
    digitHigh: options.digitHigh,
    status: "active",
  };

  state.trades.push(contract);
  return contract;
}

/**
 * Activate an existing contract
 */
export function activateContract(
  state: State,
  contractId: string | number,
  options: {
    activationPrice?: number;
    activationTime?: number;
    expiryTime?: number;
    barrier?: number;
    barrierLow?: number;
    barrierHigh?: number;
    digitTarget?: number;
    digitLow?: number;
    digitHigh?: number;
  }
): TradeMarker | undefined {
  const contract = findContractById(state, contractId);
  if (!contract) return undefined;

  if (options.activationPrice !== undefined) contract.activationPrice = options.activationPrice;
  if (options.activationTime !== undefined) contract.activationTime = options.activationTime;
  if (options.expiryTime !== undefined) contract.expiryTime = options.expiryTime;
  if (options.barrier !== undefined) contract.barrier = options.barrier;
  if (options.barrierLow !== undefined) contract.barrierLow = options.barrierLow;
  if (options.barrierHigh !== undefined) contract.barrierHigh = options.barrierHigh;
  if (options.digitTarget !== undefined) contract.digitTarget = options.digitTarget;
  if (options.digitLow !== undefined) contract.digitLow = options.digitLow;
  if (options.digitHigh !== undefined) contract.digitHigh = options.digitHigh;

  return contract;
}

/**
 * Settle a contract
 */
export function settleContract(
  state: State,
  contractId: string | number,
  options: {
    exitPrice?: number;
    result?: "win" | "loss" | "won" | "lost";
    settledTime?: number;
    settlementTickId?: string;
  }
): TradeMarker | undefined {
  const contract = findContractById(state, contractId);
  if (!contract) return undefined;

  if (options.exitPrice !== undefined) contract.exitPrice = options.exitPrice;
  if (options.result) {
    const resultLower = options.result.toLowerCase();
    contract.status = (resultLower === "win" || resultLower === "won") ? "won" : "lost";
  }
  if (options.settledTime !== undefined) contract.resolvedAt = options.settledTime;
  if (options.settlementTickId !== undefined) contract.settlementTickId = options.settlementTickId;
  contract.resultSource = options.result ? "server" : "chart";

  return contract;
}

/**
 * Update contract parameters
 */
export function updateContract(
  state: State,
  contractId: string | number,
  options: {
    barrier?: number;
    barrierLow?: number;
    barrierHigh?: number;
    digitTarget?: number;
    digitLow?: number;
    digitHigh?: number;
    duration?: number;
    durationUnit?: TradeDurationUnit;
    expiryTime?: number;
  }
): TradeMarker | undefined {
  const contract = findContractById(state, contractId);
  if (!contract) return undefined;

  if (options.barrier !== undefined) contract.barrier = options.barrier;
  if (options.barrierLow !== undefined) contract.barrierLow = options.barrierLow;
  if (options.barrierHigh !== undefined) contract.barrierHigh = options.barrierHigh;
  if (options.digitTarget !== undefined) contract.digitTarget = options.digitTarget;
  if (options.digitLow !== undefined) contract.digitLow = options.digitLow;
  if (options.digitHigh !== undefined) contract.digitHigh = options.digitHigh;
  if (options.duration !== undefined) {
    contract.durationValue = options.duration;
    contract.durationUnit = options.durationUnit ?? contract.durationUnit;
    contract.durationMs = durationToMs(options.duration, contract.durationUnit);
  }
  if (options.expiryTime !== undefined) contract.expiryTime = options.expiryTime;

  return contract;
}